/**
 * A WAY THROUGH A LONG LIST THAT IS NOT SCROLLING — #310.
 *
 * Vinnie, 30 August 2026, walking [[#296]]: *"should have pages as it is a lot
 * to scroll even at 200. The filter should remain. Another filter showing a
 * drop down to show 50, 100, etc., should be there. Or page number buttons at
 * the bottom can be clicked to go through the other pages instead of
 * scrolling."* Told that pages on Activity alone would leave the two trails
 * read by two different mechanisms, he chose consistency: **"pages on both,
 * keep it consistent."**
 *
 * He offered the page size and the page numbers as alternatives. Consistency
 * argues for the same pair everywhere rather than one on each, so both are
 * here and both are on every list that uses this.
 *
 * ⚠️ WHAT THIS DOES NOT REOPEN. [[#294]]'s fault was the SILENT cut — fifty
 * rows drawn, hundreds withheld, no count and no way on. That is fixed and
 * stays fixed whichever way the reader moves. This changes the mechanism above
 * a fixed screen, not the fix, and the count goes nowhere.
 *
 * ⛔ AND IT SUPERSEDES A DECISION MADE AN HOUR EARLIER THE SAME DAY. Walking
 * #294 he said *"the cap is nice and then I can show more if need be"*, and
 * Show more was recorded as settled. The quote above replaced it. Anybody
 * reading #294 should not take its wording as current.
 */

/**
 * Offered sizes.
 *
 * FIFTY FIRST, because that is what both screens already drew and changing how
 * much somebody sees by default is a different decision from giving them a way
 * through it. The largest is deliberately not "everything": a list that can
 * draw four hundred rows will be asked to, on a phone, and the way that fails
 * is a screen that stops responding rather than one that says no.
 */
export const PAGE_SIZES = [25, 50, 100, 200] as const;
export const A_PAGE = 50;

/** At or under this many pages, every one is offered and there are no gaps. */
const ALL_OF_THEM = 7;

export interface Page {
  /** 1-based, and clamped into range — see `at`. */
  page: number;
  pages: number;
  /** 1-based, inclusive, for the sentence. Both 0 when there is nothing. */
  from: number;
  to: number;
  /** Where to cut the list. */
  start: number;
  end: number;
}

/**
 * Which slice to draw, given how many there are.
 *
 * CLAMPED RATHER THAN TRUSTED, and that is the bug this shape exists to
 * prevent. Filtering while on page 6 of 6 can leave 2 pages, and a screen
 * holding `page = 6` against a 2-page list draws nothing at all — an empty
 * list that looks exactly like "there is nothing here". Every caller keeps its
 * own page number and every one of them can go stale, so the clamping lives
 * here and not in each of them.
 */
export function at(total: number, size: number, page: number): Page {
  const pages = Math.max(1, Math.ceil(total / Math.max(1, size)));
  const on = Math.min(Math.max(1, Math.floor(page) || 1), pages);
  const start = (on - 1) * size;
  const end = Math.min(start + size, total);
  return { page: on, pages, from: total === 0 ? 0 : start + 1, to: end, start, end };
}

/**
 * The page numbers to draw, with gaps where they have been left out.
 *
 * A GAP IS A `null`, not a string, so the screen decides how a gap looks and
 * this decides where one is. Always the first and the last, always the
 * neighbours of where you are — so the two ends of the list are one press away
 * from anywhere, which is the whole reason somebody prefers this to scrolling.
 */
export function numbers(page: number, pages: number, around = 1): (number | null)[] {
  /*
   * A SHORT LIST IS DRAWN WHOLE. Five pages came out as "1 2 … 5", which hides
   * two numbers to save one character and makes the reader work out what is
   * behind the gap. A gap has to earn its place.
   */
  if (pages <= ALL_OF_THEM) return Array.from({ length: pages }, (_, i) => i + 1);
  const wanted = new Set<number>([1, pages]);
  for (let p = page - around; p <= page + around; p += 1) {
    if (p >= 1 && p <= pages) wanted.add(p);
  }
  const sorted = [...wanted].sort((a, b) => a - b);
  const out: (number | null)[] = [];
  let last = 0;
  for (const p of sorted) {
    /* One or two missing numbers are drawn rather than hidden: "1 … 4" is no
       shorter than "1 2 3 4" and tells you less. */
    if (last && p - last <= 3) for (let m = last + 1; m < p; m += 1) out.push(m);
    else if (last) out.push(null);
    out.push(p);
    last = p;
  }
  return out;
}
